import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router } from "@angular/router";
import { Subscription, Observable, combineLatest } from "rxjs";
import { tap, concatMap } from "rxjs/operators";

import { ModuleGroupService } from "../../../common/services/modulegroup.service";
import { deepClone } from "../../../builder/models/editor.model";
import { Module } from "../../../common/models/module.model";
import { ModuleGroup } from "../../../common/models/modulegroup.model";
import { NavigationReference } from "../../../common/models/navigationreference.model";
import { ReferenceType } from "../../../common/models/referencetype.enum";
import { Organization } from "../../../common/models/organization.model";
import { Address } from "../../../common/models/address.model";
import { SubHeaderService } from "../../../core/services/sub-header.service";
import { SubHeader } from "../../../core/models/sub-header.model";
import { DynamicDialogService } from "../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../core/models/dialog.model";
import { WorkOrderService } from "../../../common/services/work-order.service";
import { WorkOrder, WorkOrderType, WorkOrderStatus } from "../../../common/models/work-order.model";
import { AuthService } from "../../../services/auth.service";
import { LunaClaimValues } from "../../../models/user-identity.model";
import { OnBehalfSteps,  } from "../../models/on-behalf.model";
import { FlowOnBehalfService } from "../../../flow/services/flow-on-behalf.service";
import { LoaderService } from "../../../common/services/loader.service";

@Component({
    selector: 'app-on-behalf-wrapper',
    templateUrl: './on-behalf-wrapper.component.html',
    styleUrls: ['./on-behalf-wrapper.component.scss'],
    standalone: false
})
export class OnBehalfWrapperComponent implements OnInit, OnDestroy {

    applicationSubmittedDialog: Dialog = new Dialog("to be filled in by the executing function", [new DialogButton("OK", true, () => this.routeHome())]);
    applicationNumber: string = "";
    get applicationTitle(): string {
        if (!this.applicationType || this.applicationType.id === "") return "";
        return this.applicationType.title;
    }
    applicationType: NavigationReference;
    availableModules: Module[] = [];
    
    currentStep: OnBehalfSteps = OnBehalfSteps.SelectSubmission;

    private _defaultApplication: NavigationReference = { id: "", title: "--Select a Submission Type--", referenceType: ReferenceType.Workflow, showEditWorkflow: false } as NavigationReference;
    private _defaultModule: Module = { id: "", isDefault: false, title: "--Select a Submission--", navigationItems: [] } as Module;
    private _defaultOrganization: Organization = { id: "", parentOrganizationId: "", isInternal: false, isLegacy: false, name: "", isActive: true, typeCode: "", address: { street: "", suite: "", poBox: "", city: "", stateCode: "", zipCode: "" } as Address } as Organization;
    private _disallowedOnBehalfModules: string[] = [ "paper", "issues" ];
    private _module: Module;
    @ViewChild("downloadLink", { static: true }) download: ElementRef;

    errorDialog: Dialog = new Dialog("An error has occurred.", [new DialogButton("OK", false, () => this.onDialogClose())]);

    get module(): Module {
        if (!this._module) {
            this._module = deepClone(this._defaultModule);
        }
        return this._module;
    }

    set module(val: Module) {
        this._module = val;
    }

    moduleGroups: ModuleGroup[];

    get moduleTitle(): string {
        if (this.module.id === this._defaultModule.id) return "";
        return this.module.title;
    }

    notImplementedDialog: Dialog = new Dialog("This function has not yet been implemented.", [new DialogButton("Sorry!", true, () => this.onDialogClose())]);

    get organizationName(): string {
        if (!this.organization || this.organization.id === this._defaultOrganization.id) return "";
        return this.organization.name;
    }

    get organizationNumber(): string {
        if (this.organization && this.organization.id !== this._defaultOrganization.id) {
            return this.organization.id;
        }
        return "";
    }

    organization: Organization = this._defaultOrganization;

    steps = OnBehalfSteps;

    subheader: SubHeader = new SubHeader({ title: "Paper Submissions", buttons: [] });
    subscription: Subscription = new Subscription();

    get urlComponent(): string {
        if (this.applicationType) return this.applicationType.uriComponent;
        return "";
    }

    constructor(
        public router: Router,
        readonly authService: AuthService,
        private moduleService: ModuleGroupService,
        private subheaderService: SubHeaderService,
        readonly loader: LoaderService,
        readonly dialogService: DynamicDialogService) { }

    ngOnInit() {
        this.subheaderService.updateSubHeader(this.subheader);
        let module$ = this.moduleService.getAll().pipe(tap((data) => {
            this.moduleGroups = data;
            let organizationModule = data.find(d => d.title.toLocaleLowerCase() === "organization");
            if (organizationModule) {
                this.availableModules = organizationModule.modules.filter(m => this._disallowedOnBehalfModules.indexOf(m.title.toLocaleLowerCase()) === -1);
            }
            let submissionModule = data.find(d => d.title.toLocaleLowerCase() === "submissions");
            if (submissionModule) {                
                this.availableModules = this.availableModules.concat(submissionModule.modules.filter(m => this._disallowedOnBehalfModules.indexOf(m.title.toLocaleLowerCase()) === -1));
                this.availableModules = this.filterModulesOnPermissions(this.availableModules);
                this.availableModules.unshift(this._defaultModule);
                this.applicationType = this._defaultApplication;
            }
        })).subscribe(
            () => {
                console.log("module groups loaded");
            },
            (error) => { console.error(error); }
        );
        this.subscription.add(module$);
        
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    advanceStep(stepNumber: OnBehalfSteps): void {
        this.currentStep = stepNumber;
    }

    applicationSubmitted(): void {
        this.applicationSubmittedDialog.message = `Application #${this.applicationNumber} of type ${this.applicationTitle} on behalf of ${this.organizationName} has been created.`;
        this.applicationSubmittedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.applicationSubmittedDialog);
    }

    filterModulesOnPermissions(modules: Module[]): Module[] {
        let permittedModules: Module[] = [];
        if (this.authService.hasSystemPermission(LunaClaimValues.ExecutePermitFlowOnBehalf)) {
            permittedModules = permittedModules.concat(modules.filter(m => m.title.toLocaleLowerCase().indexOf("permitting") !== -1));
        }
        if (this.authService.hasSystemPermission(LunaClaimValues.ExecuteWellTransferOnBehalf)) {
            permittedModules = permittedModules.concat(modules.filter(m => m.title.toLocaleLowerCase().indexOf("transfer") !== -1));
        }
        if (this.authService.hasSystemPermission(LunaClaimValues.ExecuteFAFlowOnBehalf)) {
            permittedModules = permittedModules.concat(modules.filter(m => m.title.toLocaleLowerCase().indexOf("financial assurance") !== -1));
        }
        if (this.authService.hasSystemPermission(LunaClaimValues.ExecuteRegistrationOnBehalf)) {
            permittedModules = permittedModules.concat(modules.filter(m => m.title.toLocaleLowerCase().indexOf("registration") !== -1 || m.title.toLocaleLowerCase().indexOf("organization") !== -1));
        }

        permittedModules = permittedModules.sort((a, b) => {
            if (a.title < b.title) return -1;
            if (b.title < a.title) return 1;
            return 0;
        });
        return permittedModules;
    }

    handleError(error: any): void {
        this.errorDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.errorDialog);
    }

    notImplemented(): void {
        this.notImplementedDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.notImplementedDialog);
    }

    onApplicationChanged(newApp: NavigationReference): void {
        this.applicationType = newApp;
    }

    onDialogClose(): void { }

    onModuleChanged(newModule: Module): void {
        this.module = newModule;
        this.applicationType = this._defaultApplication;
    }

    onOrganizationSelected(org: Organization): void {
        this.organization = org;
    }

    routeHome(): void {
        this.router.navigate(["/home"]);
    }

    setApplication(application: string): void {
        this.applicationNumber = application;
    }

    setStep(step: OnBehalfSteps): void {
        if (step === OnBehalfSteps.SelectOrganization) {
            if (this.module.id === this._defaultModule.id || this.applicationType.id === this._defaultApplication.id) return;
        } else if (step === OnBehalfSteps.GenerateApplicationNumber || step === OnBehalfSteps.GenerateScanAndSubmit) {
            if (this.organization.id === "") return;
        }
        this.currentStep = step;
    }
}
